'use client';

import { c as _c } from "react/compiler-runtime";
import { parseAnswerState } from '@repo/contracts/cardfinder/funnel/schema';
import { type Dispatch, useEffect, useRef } from 'react';
import type { AnswerAction } from '@/entities/answer/model/actions';
import type { AnswerStore } from '@/entities/answer/model/types';
import type { BranchId } from '@/shared/config/funnel/constants';
import { readSession, writeSession } from '@/shared/lib/sessionStorage';
import { ANSWERS_KEY } from './localSession';
type UseLocalAnswerHydrationParams = {
  branchId: BranchId | null;
  store: AnswerStore;
  dispatch: Dispatch<AnswerAction>;
};

/**
 * Hydrates the answer store from local sessionStorage once per branch, and
 * mirrors answers back to sessionStorage as they change. Resets hydration state
 * on branch change so consumers re-defer until re-hydrated.
 *
 * Hydration is entirely local; the only remote read belongs to the
 * recommendations page. The mirror below is what carries answers across the
 * branch-change DEHYDRATE when the user leaves the last funnel step for the
 * recommendations page (where `branchId` becomes null), so it must stay
 * synchronous with the answer state.
 */
export function useLocalAnswerHydration(t0) {
  const $ = _c(14);
  const {
    branchId,
    store,
    dispatch
  } = t0;
  const hydratedBranchRef = useRef(undefined);
  let t1;
  if ($[0] !== dispatch) {
    t1 = () => () => {
      hydratedBranchRef.current = undefined;
      dispatch({
        type: "DEHYDRATE"
      });
    };
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== branchId || $[3] !== dispatch) {
    t2 = [branchId, dispatch];
    $[2] = branchId;
    $[3] = dispatch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== store.answers || $[6] !== store.isHydrated) {
    t3 = () => {
      if (!store.isHydrated) {
        return;
      }
      writeSession(ANSWERS_KEY, store.answers);
    };
    t4 = [store.answers, store.isHydrated];
    $[5] = store.answers;
    $[6] = store.isHydrated;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== branchId || $[10] !== dispatch || $[11] !== store.isHydrated) {
    t5 = () => {
      if (hydratedBranchRef.current === branchId && store.isHydrated) {
        return;
      }
      hydratedBranchRef.current = branchId;
      const storedRaw = readSession(ANSWERS_KEY);
      const local = storedRaw !== undefined ? parseAnswerState(storedRaw) : {};
      dispatch({
        type: "HYDRATE",
        answers: local,
        branchId
      });
    };
    t6 = [branchId, dispatch, store.isHydrated];
    $[9] = branchId;
    $[10] = dispatch;
    $[11] = store.isHydrated;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
}